import React, { useCallback, useEffect, useState } from "react";
import {
  getSchedulingErrorMessage,
  getSchedulingSettings,
  listProviders,
  listServices,
} from "../../../api/schedulingApi";
import AppointmentsPanel from "./AppointmentsPanel";
import ProvidersPanel from "./ProvidersPanel";
import ServicesPanel from "./ServicesPanel";
import SettingsPanel from "./SettingsPanel";
import WhatsAppNotificationsPanel from "../notifications/WhatsAppNotificationsPanel";
import { Alert } from "./ui";
import { Link } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { useBotWorkspace } from "../BotWorkspace";

const SECTIONS = [
  ["appointments", "Appointments"],
  ["providers", "Providers & hours"],
  ["services", "Services"],
  ["settings", "Settings"],
  ["notifications", "WhatsApp notifications"],
];

export default function SchedulingTab({ botId, activeSection, directLinks = false, hideNavigation = false }) {
  const { can } = useAuth();
  const appointmentConfigRead = can("appointment_configuration.read");
  const appointmentConfigUpdate = can("appointment_configuration.update");
  const servicesRead = can("services.read") || can("services.manage")
    || can("providers.update") || can("providers.manage")
    || appointmentConfigUpdate
    || can("booking_lookups.read") || appointmentConfigRead;
  const servicesManage = can("services.manage") || appointmentConfigUpdate;
  const providersRead = can("providers.read") || can("providers.update") || can("providers.manage") || can("provider_hours.read")
    || can("provider_hours.manage") || can("booking_lookups.read") || appointmentConfigRead || appointmentConfigUpdate;
  const providersManage = can("providers.update") || can("providers.manage") || appointmentConfigUpdate;
  const providersCreate = can("providers.manage") || appointmentConfigUpdate;
  const providerHoursRead = can("provider_hours.read") || can("provider_hours.manage")
    || appointmentConfigRead || appointmentConfigUpdate;
  const providerHoursManage = can("provider_hours.manage") || appointmentConfigUpdate;
  const notificationRead = can("notification_configuration.read");
  const workspace = useBotWorkspace();
  const useDirectSchedulingRoutes = directLinks && !appointmentConfigRead;
  const operationalTimezone = workspace?.timezone || "UTC";
  const [localSection, setSection] = useState(appointmentConfigRead || can("appointments.read")
    ? "appointments" : providersRead ? "providers" : "services");
  const section = activeSection || localSection;
  const loadSettings = appointmentConfigRead && !hideNavigation;
  const loadProviderList = providersRead && (!hideNavigation || section === "providers");
  const [settings, setSettings] = useState(null);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState("");

  const loadProviders = useCallback(async () => setProviders((await listProviders(botId)) || []), [botId]);
  const loadServices = useCallback(async () => setServices((await listServices(botId)) || []), [botId]);

  useEffect(() => {
    let active = true;
    Promise.all([
      loadSettings
        ? getSchedulingSettings(botId)
        : Promise.resolve({ timezone: operationalTimezone }),
      loadProviderList ? listProviders(botId) : Promise.resolve([]),
      servicesRead ? listServices(botId) : Promise.resolve([]),
    ])
      .then(([s, p, sv]) => {
        if (!active) return;
        setSettings(s);
        setProviders(p || []);
        setServices(sv || []);
      })
      .catch((err) => active && setError(getSchedulingErrorMessage(err, "Could not load scheduling data.")));
    return () => {
      active = false;
    };
  }, [botId, loadSettings, loadProviderList, servicesRead, operationalTimezone]);

  if (error) return <Alert>{error}</Alert>;
  if (!settings) return <p className="text-sm text-slate-500">Loading scheduling…</p>;

  return (
    <div className="space-y-4">
      {!hideNavigation && <div className="flex flex-wrap gap-2" role="tablist" aria-label="Scheduling sections">
        {SECTIONS.filter(([key]) => (key === "appointments" && (can("appointments.read") || appointmentConfigRead))
          || (key === "providers" && providersRead)
          || (key === "services" && servicesRead)
          || (key === "settings" && appointmentConfigRead)
          || (key === "notifications" && notificationRead)).map(([key, label]) => (
          activeSection ? <Link key={key}
            to={useDirectSchedulingRoutes
              ? `/agents/${encodeURIComponent(botId)}/${key === "settings" ? "booking-settings" : key}`
              : `/agents/${encodeURIComponent(botId)}/agent-config?tab=appointments&section=${key}`}
            aria-current={section === key ? "page" : undefined}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold ${section === key ? "bg-[#096dea] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
            {key === "providers" && !providerHoursRead ? "Providers" : label}
          </Link> : <button
            key={key}
            type="button"
            role="tab"
            aria-selected={section === key}
            onClick={() => setSection(key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
              section === key ? "bg-[#096dea] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
            }`}
          >
            {key === "providers" && !providerHoursRead ? "Providers" : label}
          </button>
        ))}
      </div>}

      {(can("appointments.read") || appointmentConfigRead) && section === "appointments"
        && <AppointmentsPanel botId={botId} timeZone={settings.timezone || "UTC"} providers={providers} services={services} />}
      {providersRead && section === "providers" && <ProvidersPanel botId={botId} providers={providers} services={services} onProvidersChanged={loadProviders} canManageProviders={providersManage} canCreateProviders={providersCreate} canReadHours={providerHoursRead} canManageHours={providerHoursManage} />}
      {servicesRead && section === "services" && <ServicesPanel botId={botId} services={services} onServicesChanged={loadServices} readOnly={!servicesManage} />}
      {appointmentConfigRead && section === "settings" && <SettingsPanel botId={botId} settings={settings} onSaved={setSettings} readOnly={!appointmentConfigUpdate} canUpdate={appointmentConfigUpdate} />}
      {notificationRead && section === "notifications" && <WhatsAppNotificationsPanel
        botId={botId}
        readOnly={!can("notification_configuration.update")}
        eventKeys={["appointment_confirmed"]}
      />}
    </div>
  );
}
